/* eslint-disable no-unused-vars */
import i18n from "@/i18n";
import moment from "moment";
import numeral from "numeral";
import swal from "sweetalert";
import { isProxy, toRaw } from "vue";
import { da } from "vuetify/locale";

export function customFilter(value, query, item) {
  // Array values (ex: serials, qr_codes) are searched as one string
  if (Array.isArray(value)) {
    value = value.filter((v) => !isNone(v)).join(" ");
  }
  if (isNone(value) ||
    isNone(query) ||
    typeof value !== 'string' ||
    typeof query !== 'string') {
     return false;
  }
  const normalizeText = (text) =>
    text
      .normalize('NFD') // Decompose combined characters into base and accents
      .replace(/[̀-ͯ]/g, '') // Remove diacritical marks
      .toLowerCase(); // Convert to lowercase for case-insensitive matching
  const normalizedQuery = normalizeText(query);
  const normalizedItem = normalizeText(value);
  const searchWords = normalizedQuery.split(/\s+/);
  return searchWords.every(word => normalizedItem.includes(word));
}

export function capitalize(value) {
  // Capitalize a string
  if (!value) return "";
  value = value.toString();
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatAvailability(value) {
  const val = numeral(value);
  if (isNone(value) || isNone(val)) {
    return "?";
  }
  return val.format("0");
}

export function formatBool(value) {
  if (value) {
    return i18n.global.t("generic.yes");
  }
  return i18n.global.t("generic.no");
}

export function formatCategory(value) {
  if (isNone(value) || isNone(value.producer_types)) {
    return "";
  }
  var categories = value.producer_types.map((x) => x.name);
  if (categories.length === 0) {
    categories = [capitalize(i18n.global.t("part_categories.generic"))];
  }
  return `${value.name} (${categories.join(", ")})`;
}

export function formatCategoryEn(value) {
  if (isNone(value) || isNone(value.producer_types)) {
    return "";
  }
  var categories = value.producer_types.map((x) => x.name);
  if (categories.length === 0) {
    categories = [capitalize(i18n.global.t("part_categories.generic"))];
  }
  return `${value.name_en} (${categories.join(", ")})`;
}

export function formatDat(value) {
  if (isNone(value)) {
    return "";
  }
  return moment(value).format("DD/MM");
}

export function formatDate(value) {
  if (isNone(value)) {
    return "";
  }
  return moment(value).format("DD/MM/YYYY");
}

export function formatDateTime(value) {
  if (isNone(value)) {
    return "";
  }
  return moment(value).format("DD/MM/YYYY HH:mm");
}

export function formatDateCustom(dateString) {
  const [datePart] = dateString.split('T');
  return datePart;
}

export function formatDateTimeCustom(dateString) {
  const [datePart, timePart] = dateString.split('T');
  const [hours, minutes, seconds] = timePart.replace('Z', '').split(':');
  const [year, month, day] = datePart.split('-');
  return `${year}-${day}-${month} ${hours}:${minutes}`;
}


export function formatFullDateTime(value) {
  if (isNone(value)) {
    return "";
  }
  return moment(value).format("DD/MM/YYYY HH:mm:ss");
}

export function formatTime(value) {
  if (isNone(value)) {
    return "";
  }
  return moment(value).format("HH:mm");
}

export function formatFloat(value) {
  const val = numeral(value);
  if (isNone(value) || isNone(val)) {
    return "?";
  }
  return val.format("0.0");
}

export function formatOrigin(value) {
  if (isNone(value)) {
    return "";
  }
  return i18n.global.t(`limitations.origin.${value}`);
}

export function formatPercentage(value) {
  var val = numeral(value);
  if (isNone(value) || isNone(val)) {
    return "?";
  }
  val = numeral(value * 100);
  return val.format("0.0");
}

export function formatPeriod(value) {
  switch (value) {
    case "an":
      return i18n.global.t("preventive_maintenances.period.annual");
    case "bian":
      return i18n.global.t("preventive_maintenances.period.biannual");
    case "quart":
      return i18n.global.t("preventive_maintenances.period.quarterly");
    case "four":
      return i18n.global.t("preventive_maintenances.period.four");
    default:
      return i18n.global.t("preventive_maintenances.period.unknown");
  }
}

export function formatPlace(item) {
  if (!item.stockage) return ''
  let place = item.stockage.name + " - "
  if (item.shelf) return (place + item.cabinet + "/" + item.shelf)
  return (place + item.cabinet)
}

export function formatPrice(value, currency) {
  var prefix = null;
  var postfix = null;
  switch (currency) {
    case "EUR":
      postfix = "€";
      break;
    case "USD":
      postfix = "$US";
      break;
    case "NAD":
      prefix = "N$";
      break;
    case "KMF":
      postfix = "KMF";
      break;
    default:
      console.log(`Unknown currency ${currency}.`);
  }
  var ret = formatFloat(value);
  if (prefix !== null) {
    ret = `${prefix} ${ret}`;
  }
  if (postfix !== null) {
    ret = `${ret} ${postfix}`;
  }
  return ret;
}

export function formatVariety(value) {
  if (value === "s") {
    return i18n.global.t("variety.solar");
  } else if (value === "w") {
    return i18n.global.t("variety.wind");
  }
  return i18n.global.t("variety.all");
}

export function affectationKindFilter(value) {
  // Convert affectation kind to full string
  if (value === "er") return i18n.global.t("affectations.status.external");
  else if (value === "ir") return i18n.global.t("affectations.status.internal");
  else if (value === "ss") return i18n.global.t("affectations.status.stock");
  else if (value === "ts") return i18n.global.t("affectations.status.trash");
  else if (value === "p") return i18n.global.t("affectations.status.producer");
  return "";
}

export function criticalityFilter(value) {
  // Convert criticality to full string
  if (value === "crit") return i18n.global.t("alarm_codes.criticality.critical");
  else if (value === "warn") return i18n.global.t("alarm_codes.criticality.warning");
  else if (value === "ok") return i18n.global.t("alarm_codes.criticality.ok");
  return "";
}

export function reportStatusFilter(value) {
  // Convert intervention report status values to full string
  if (value === "tmp") return i18n.global.t("intervention_reports.status.tmp");
  else if (value === "final")
    return i18n.global.t("intervention_reports.status.final");
  else if (value === "maintenance")
    return i18n.global.t("intervention_reports.status.maintenance");
  else if (value === "none") return i18n.global.t("intervention_reports.status.none");
  return "";
}

export function unityFilter(value) {
  // Convert unity values to full string
  if (value === "box") return i18n.global.t("unity.box");
  else if (value === "l") return i18n.global.t("unity.liter");
  else if (value === "m") return i18n.global.t("unity.meter");
  else if (value === "kg") return i18n.global.t("unity.kg");
  else if (value === "pi") return i18n.global.t("unity.piece");
  else if (value === "un") return i18n.global.t("unity.unit");
  return "";
}

export function varietyFilter(value) {
  // Convert variety values to full string
  if (value === "s") return i18n.global.t("variety.solar");
  else if (value === "w") return i18n.global.t("variety.wind");
  return "";
}

export function extractFilename(headers, default_ = "file") {
  // Read filename from a downloaded file stream
  let headerLine = headers["content-disposition"];
  const fnReg = /filename="(.+)"/g;
  var match = fnReg.exec(headerLine);
  var filename = default_;
  if (match !== null && match.length > 1) {
    filename = match[1];
  }

  return filename;
}

export function isNone(value) {
  // Check if a value is null in a wild sens
  return ["", null, undefined, NaN].includes(value);
}

export function isEmpty(value) {
  // Check if a value is an empty list or None (as defined before)
  return value == [] || isNone(value);
}

export function slicyUpdate(array_, item) {
  // Create or Update array item (based on pk key match)
  // Usefull to trigger front update
  const index = array_.findIndex((obj) => obj.pk === item.pk);
  if (index !== -1) {
    array_.splice(index, 1);
    array_.splice(index, 0, item);
  } else {
    array_.push(item);
  }
}

export function slicyRemove(array_, item) {
  // Remove array item (based on pk key match)
  // Usefull to trigger front update
  const index = array_.findIndex((obj) => obj.pk === item.pk);
  if (index !== -1) {
    array_.splice(index, 1);
  }
}

export function concat(...values) {
  // Concatenate all inputed elements with a full "cadratin"
  return [...values].join(" — ");
}

export function isUnit(stock_part) {
  // Check if a stock part is a unit (aka with serial number)
  return stock_part.unity === "un";
}

export function endCorrection(array) {
  var array_ = array;
  // adapt end time to bypass Bryntum scheduler tz manipulation error
  for (let i = 0; i < array.length; i++) {
    const start = moment(array_[i].startDate);
    const end = moment(array_[i].endDate);
    if (
      start.format("mmss") === "0000" &&
      end.format("mmss") === "5959" &&
      end.hour() <= 3
    ) {
      // Whole day events can have a shifted end due to Scheduler bug on tz manipulation
      // we correct that specific new events
      array_[i].endDate = end.subtract(1, "days").endOf("day");
    }
  }
  return array_;
}

export function sumValues(items) {
  var val = 0.0;
  Object.values(items).forEach((value) => {
    val += value;
  });
  return val;
}

export function alertPopup(text) {
  return swal({
    text: text,
    icon: "warning",
    buttons: [capitalize(i18n.global.t("cancel")), "OK"]
  });
}

export function unProxy(value) {
  if (isProxy(value)) {
    value = toRaw(value);
  }
  return value;
}

// Grafana dashboards reading the raijin database (grafana/solar in the raijin repo)
const GRAFANA = "https://grafana.innovent.fr";

export function grafanaSolarProducerUrl(producerId, range = "now-24h") {
  return `${GRAFANA}/d/raijin-solar-producer?orgId=1&var-producer=${producerId}&from=${range}&to=now`;
}

export function grafanaSolarPowerStationUrl(powerStationId, range = "now-24h") {
  return `${GRAFANA}/d/raijin-solar-power-station?orgId=1&var-power_station=${powerStationId}&from=${range}&to=now`;
}

// Requires [security] allow_embedding = true on the Grafana server
// (GF_SECURITY_ALLOW_EMBEDDING=true), otherwise the panels show a link instead.
export const GRAFANA_EMBED_ENABLED = true;

// Single panels of those dashboards, for iframes (d-solo)
export const GRAFANA_SOLAR_PANELS = {
  producer_power: 10, // power and injection
  producer_irradiance: 11, // vertical and horizontal irradiance
  producer_expected: 12, // power against the expected range
  station_per_producer: 11, // power per producer, stacked
};

export function grafanaSolarProducerPanelUrl(producerId, panelId, range = "now-24h") {
  return `${GRAFANA}/d-solo/raijin-solar-producer?orgId=1&var-producer=${producerId}&panelId=${panelId}&from=${range}&to=now&theme=light`;
}

// Enedis PADT injection dashboard (raijin-enedis-injection): one installation,
// production and/or consumption. from/to: Grafana time expressions or epoch ms.
export function grafanaEnedisInjectionUrl(installationId, direction = "PROD", from = "now-30d", to = "now") {
  return `${GRAFANA}/d/raijin-enedis-injection?orgId=1&var-installation=${installationId}&var-direction=${direction}&from=${from}&to=${to}`;
}

export const GRAFANA_ENEDIS_PANELS = {
  power: 10, // 5-minute average power
  daily_energy: 11, // energy per day, bars
  monthly: 12, // monthly summary table
  overview: 20, // all installations ranked by production energy
  cumulative: 30, // cumulative production against 80 % of the monthly profile
  split: 31, // production split: fixed-price share and spot share
  soregies: 40, // daily energy, Enedis index against the Soregies production
};

// Whole dashboard without a chosen installation (Grafana picks the first one)
export function grafanaEnedisInjectionOverviewUrl(from = "now-30d", to = "now") {
  return `${GRAFANA}/d/raijin-enedis-injection?orgId=1&from=${from}&to=${to}`;
}

export function grafanaEnedisInjectionOverviewPanelUrl(from = "now-30d", to = "now") {
  return `${GRAFANA}/d-solo/raijin-enedis-injection?orgId=1&panelId=${GRAFANA_ENEDIS_PANELS.overview}&from=${from}&to=${to}&theme=light`;
}

export function grafanaEnedisInjectionPanelUrl(installationId, panelId, direction = "PROD", from = "now-30d", to = "now") {
  return `${GRAFANA}/d-solo/raijin-enedis-injection?orgId=1&var-installation=${installationId}&var-direction=${direction}&panelId=${panelId}&from=${from}&to=${to}&theme=light`;
}

export function grafanaWindProducerUrl(producerId, range = "now-24h") {
  return `${GRAFANA}/d/raijin-wind-producer?orgId=1&var-producer=${producerId}&from=${range}&to=now`;
}

export function grafanaWindPowerStationUrl(powerStationId, range = "now-24h") {
  return `${GRAFANA}/d/raijin-wind-power-station?orgId=1&var-power_station=${powerStationId}&from=${range}&to=now`;
}

export const GRAFANA_WIND_PANELS = {
  producer_power_wind: 10, // power, theoretical power and wind (right axis)
  producer_curves: 14, // min and mean power curves with measured points
  station_power: 10, // station total power with mean wind (right axis)
  station_per_producer: 11, // power per producer, stacked, with wind per producer
};

export function grafanaWindPowerStationPanelUrl(powerStationId, panelId, range = "now-24h") {
  return `${GRAFANA}/d-solo/raijin-wind-power-station?orgId=1&var-power_station=${powerStationId}&panelId=${panelId}&from=${range}&to=now&theme=light`;
}

export function grafanaWindProducerPanelUrl(producerId, panelId, range = "now-24h") {
  return `${GRAFANA}/d-solo/raijin-wind-producer?orgId=1&var-producer=${producerId}&panelId=${panelId}&from=${range}&to=now&theme=light`;
}

export const GRAFANA_BATTERY_PANELS = {
  soc: 10, // state of charge
  power: 11, // battery power, charge < 0 < discharge
  frequency: 12, // frequency and grid setpoint
  energy: 13, // energy left against capacity
};

export function grafanaBatteryUrl(batteryId, range = "now-24h") {
  return `${GRAFANA}/d/raijin-battery?orgId=1&var-battery=${batteryId}&from=${range}&to=now`;
}

export function grafanaBatteryPanelUrl(batteryId, panelId, range = "now-24h") {
  return `${GRAFANA}/d-solo/raijin-battery?orgId=1&var-battery=${batteryId}&panelId=${panelId}&from=${range}&to=now&theme=light`;
}

export function grafanaSolarPowerStationPanelUrl(powerStationId, panelId, range = "now-24h") {
  return `${GRAFANA}/d-solo/raijin-solar-power-station?orgId=1&var-power_station=${powerStationId}&panelId=${panelId}&from=${range}&to=now&theme=light`;
}

// Regions a user works in, from the stockages assigned to them (stockages carry
// their assigned user ids). A stockage without a region value falls back to the
// region whose name matches the stockage name, case-insensitive.
export function defaultRegionsForUser(stockages, userId, regions) {
  const mine = (stockages || []).filter((st) => Array.isArray(st.user_ids) && st.user_ids.includes(userId));
  const regionIds = (regions || []).map((r) => r.id);
  const found = new Set();
  mine.forEach((st) => {
    if (st.region && regionIds.includes(st.region)) {
      found.add(st.region);
      return;
    }
    const byName = regionIds.find((id) => String(id).toLowerCase() === String(st.name || "").toLowerCase());
    if (byName !== undefined) {
      found.add(byName);
    }
  });
  return Array.from(found);
}

// Last known regions of a user, kept in the browser so a page can preselect
// them at once while the stockages are fetched again in the background.
const REGIONS_CACHE_KEY = "fujin.user_regions";

export function cachedUserRegions(userId) {
  try {
    const all = JSON.parse(localStorage.getItem(REGIONS_CACHE_KEY) || "{}");
    const regions = all[String(userId)];
    return Array.isArray(regions) ? regions : [];
  } catch (e) {
    return [];
  }
}

export function rememberUserRegions(userId, regions) {
  try {
    const all = JSON.parse(localStorage.getItem(REGIONS_CACHE_KEY) || "{}");
    all[String(userId)] = regions;
    localStorage.setItem(REGIONS_CACHE_KEY, JSON.stringify(all));
  } catch (e) {
    // storage unavailable: the regions are simply looked up again next time
  }
}
